import {
    Box,
    Button,
    Center,
    Flex,
    HStack,
    Image,
    Spinner,
    Text,
    useDisclosure,
    useToast,
    Wrap,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { digit } from "lib/utils/digit";
import CompanyInfo from "components/CompanyInfo";
import TwoButtonModal from "components/TwoButtonModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getItemDetail, setItemCancel } from "./api";
import { convertDateFullTime } from "lib/utils/regular";
import { OrderStatus, ResStatus } from "lib/api/enum";
import PhotoButton from "components/PhotoButton";
import PreviewImageModal from "components/PreviewImageModal";
import { useState } from "react";
import withAuth from "lib/hook/withAuth";
import usePathNavigate from "lib/hook/usePathNavigate";

function ProductDetail() {
    const { productId } = useParams();
    const { backNavigate } = usePathNavigate();
    const toast = useToast();

    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery(
        ["getItemDetail", productId],
        () =>
            getItemDetail({
                item_id: Number(productId),
            }),

        {
            cacheTime: 0,
            onError: (err: any) => {
                toast({
                    title: "오류",
                    description: err.message,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top",
                });
            },
        }
    );

    const {
        isOpen: imageIsOpen,
        onClose: imageClose,
        onOpen: imageOpen,
    } = useDisclosure();
    const [previewImage, setPreviewImage] = useState("");
    const onPreviewImage = (previewImage: string) => {
        console.log(previewImage);
        setPreviewImage(previewImage);
        imageOpen();
    };

    const {
        isOpen: cancelIsOpen,
        onClose: cancelClose,
        onOpen: cancelOpen,
    } = useDisclosure();

    const onCancelOrder = async () => {
        // 동준
        console.log("cancel order");
        const result = await setItemCancel({ item_id: Number(productId) });
        if (result.status === ResStatus.RES_SUCCESS) {
            toast({
                title: "정보",
                description: "주문이 취소되었습니다",
                status: "success",
                duration: 1000,
                position: "top",
            });
            // refetch
            queryClient.refetchQueries(["getItemDetail", productId]);
        } else {
            toast({
                title: "오류",
                description: "주문을 취소할 수 없습니다",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top",
            });
        }
    };

    if (isLoading) {
        return (
            <Box w="100vw" h="100vh">
                <Center mt={"20px"} h="full">
                    <Spinner size="xl" />
                </Center>
            </Box>
        );
    }

    return (
        <Flex flexDirection={"column"}>
            <Box h="60px" position={"relative"}>
                <Center h={"full"} fontSize="20px" fontWeight={"semibold"}>
                    {data?.command.item.category_name +
                        (data?.command.item.category_name !== "가방"
                            ? " / " + data?.command.item.material_name
                            : "")}
                </Center>
                <Image
                    src="/assets/images/icn_close_header.svg"
                    position="absolute"
                    top={0}
                    bottom={0}
                    my="auto"
                    right={"16px"}
                    onClick={() => backNavigate()}
                />
            </Box>
            <Box h={"16px"}></Box>
            <Flex p="20px" flexDirection={"column"}>
                <Text fontSize={"16px"} fontWeight="semibold">
                    제품정보
                </Text>
                <HStack
                    fontSize={"13px"}
                    fontWeight="medium"
                    spacing={"43px"}
                    mt="15px"
                >
                    <Text textColor={"#616161"}>생성일시</Text>
                    <Text textColor={"#212121"}>
                        {convertDateFullTime(data?.command.item.reg_date)}
                    </Text>
                </HStack>
                <HStack
                    fontSize={"13px"}
                    fontWeight="medium"
                    spacing={"43px"}
                    mt="8px"
                >
                    <Text textColor={"#616161"}>제품번호</Text>
                    <Text textColor={"#212121"}>{productId}</Text>
                </HStack>
            </Flex>
            <Box w={"100%"} h="8px" bg="#f5f6f8" />
            <Flex p="20px" flexDirection={"column"}>
                <Text fontSize={"16px"} fontWeight="semibold">
                    {"서비스 내역"}
                </Text>
                <Text mt={"15px"} fontSize={"13px"}>
                    {data?.command.item.service_name.join(" · ")}
                </Text>
            </Flex>
            <Box w={"100%"} h="8px" bg="#f5f6f8" />
            <Flex p="20px" flexDirection={"column"}>
                <Text fontSize={"16px"} fontWeight="semibold">
                    제품사진
                </Text>
                {data?.command.item.item_status === OrderStatus.REQUESTING ||
                data?.command.item.item_status === OrderStatus.WAITING_ORDER ? (
                    <Box mt={"15px"}>
                        <Wrap spacing={"5px"} overflow="visible">
                            {data.command.item.image.before.map(
                                (photo, index) => (
                                    <PhotoButton
                                        key={index}
                                        slotId={index}
                                        type="before"
                                        imagePath={
                                            data.command.item.image.before[
                                                index
                                            ]
                                        }
                                        onPreviewImage={onPreviewImage}
                                    />
                                )
                            )}
                        </Wrap>
                    </Box>
                ) : data?.command.item.item_status ===
                      OrderStatus.MINT_COMPLETE ||
                  data?.command.item.item_status ===
                      OrderStatus.DELIVERY_COMPLETE ||
                  data?.command.item.item_status === OrderStatus.SHIPPING ? (
                    <>
                        <Box mt={"15px"}>
                            <Text fontSize={"13px"} mb="8px">
                                Before
                            </Text>
                            <Wrap spacing={"5px"} overflow="visible">
                                {data.command.item.image.before.map(
                                    (photo, index) => (
                                        <PhotoButton
                                            key={index}
                                            slotId={index}
                                            type="before"
                                            imagePath={
                                                data.command.item.image.before[
                                                    index
                                                ]
                                            }
                                            onPreviewImage={onPreviewImage}
                                        />
                                    )
                                )}
                            </Wrap>
                        </Box>
                        <Box mt={"20px"}>
                            <Text fontSize={"13px"} mb="8px">
                                After
                            </Text>
                            <Wrap spacing={"5px"} overflow="visible">
                                {data.command.item.image.after.map(
                                    (photo, index) => (
                                        <PhotoButton
                                            key={index}
                                            slotId={index}
                                            type="after"
                                            imagePath={
                                                data.command.item.image.after[
                                                    index
                                                ]
                                            }
                                            onPreviewImage={onPreviewImage}
                                        />
                                    )
                                )}
                            </Wrap>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box mt={"15px"}>
                            <Text fontSize={"13px"} mb="8px">
                                Before
                            </Text>
                            <Wrap spacing={"5px"} overflow="visible">
                                {data?.command.item.image.before.map(
                                    (photo, index) => (
                                        <PhotoButton
                                            key={index}
                                            slotId={index}
                                            type="before"
                                            imagePath={
                                                data?.command.item.image.before[
                                                    index
                                                ]
                                            }
                                            onPreviewImage={onPreviewImage}
                                        />
                                    )
                                )}
                            </Wrap>
                        </Box>
                        <Box mt={"20px"}>
                            <Text fontSize={"13px"} mb="8px">
                                After
                            </Text>
                            <Text
                                fontSize={"14px"}
                                fontWeight="semibold"
                                textColor="#128cfe"
                            >
                                민트 작업이 완료되지 않았습니다
                            </Text>
                        </Box>
                    </>
                )}
            </Flex>
            <Box w={"100%"} h="8px" bg="#f5f6f8" />
            <Flex p="20px" flexDirection={"column"}>
                <Text fontSize={"16px"} fontWeight="semibold">
                    요청사항
                </Text>
                <Box
                    mt={"15px"}
                    px={"10px"}
                    py="12px"
                    border={"1px"}
                    borderRadius="4px"
                    borderColor={"#eeeeee"}
                    bg="#fafafa"
                    minH={"108px"}
                >
                    <Text fontSize={"14px"} whiteSpace="pre-wrap">
                        {data?.command.item.item_comment}
                    </Text>
                </Box>
            </Flex>
            <Box w={"100%"} h="8px" bg="#f5f6f8" />
            <Flex px="20px" pt="20px" pb={"60px"} flexDirection={"column"}>
                <HStack
                    fontSize={"16px"}
                    fontWeight="semibold"
                    justifyContent={"space-between"}
                >
                    <Text>서비스 금액</Text>
                    {data?.command.item.item_status !==
                    OrderStatus.CANCEL_ORDER ? (
                        data?.command.item.item_status !==
                        OrderStatus.REQUESTING ? (
                            <Text>
                                {digit(Number(data?.command.item.item_price)) +
                                    " 원"}
                            </Text>
                        ) : (
                            <Text
                                fontSize={"16px"}
                                fontWeight="semibold"
                                textColor={"#fda02d"}
                            >
                                {"견적 진행 중"}
                            </Text>
                        )
                    ) : (
                        <HStack>
                            <Text
                                fontSize={"14px"}
                                fontWeight="bold"
                                textColor={"#fc375f"}
                            >
                                취소됨
                            </Text>
                            <Text
                                textColor={"#b9b9b9"}
                                textDecoration="line-through"
                            >
                                {digit(Number(data?.command.item.item_price)) +
                                    "원"}
                            </Text>
                        </HStack>
                    )}
                </HStack>
                {data?.command.item.payment_extra && (
                    <>
                        <HStack
                            fontSize={"16px"}
                            fontWeight="semibold"
                            justifyContent={"space-between"}
                        >
                            <Text>추가금액 내역</Text>
                            <Text>
                                {digit(
                                    Number(
                                        data?.command.item.payment_extra
                                            .payment_amount
                                    )
                                ) + " 원"}
                            </Text>
                        </HStack>
                        <Box
                            mt={"15px"}
                            px={"10px"}
                            py="12px"
                            border={"1px"}
                            borderRadius="4px"
                            borderColor={"#eeeeee"}
                            bg="#fafafa"
                            minH={"108px"}
                        >
                            <Text fontSize={"14px"} whiteSpace="pre-wrap">
                                {data?.command.item.payment_extra.comment}
                            </Text>
                        </Box>
                    </>
                )}
                <>
                    {data?.command.item.item_status ===
                        OrderStatus.COMPLETE_PAYMENT && (
                        <Button
                            colorScheme={"black_button"}
                            h="50px"
                            mt={"40px"}
                            onClick={cancelOpen}
                        >
                            주문취소
                        </Button>
                    )}
                </>
            </Flex>
            <CompanyInfo />
            <TwoButtonModal
                isOpen={cancelIsOpen}
                textLeft="주문취소"
                textRight="아니오"
                onClose={cancelClose}
                onClickLeftButton={onCancelOrder}
                onClickRightButton={cancelClose}
            >
                <Flex
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems="center"
                    mt={"20px"}
                >
                    <Text fontSize={"15px"} fontWeight="semibold">
                        {productId}번 제품 주문을 취소하시겠습니까?
                    </Text>
                </Flex>
            </TwoButtonModal>
            <PreviewImageModal
                isOpen={imageIsOpen}
                onClose={imageClose}
                previewImage={previewImage}
            />
        </Flex>
    );
}
export default withAuth(ProductDetail);
